/**
 * WeMetRecapCta — the recap surfaces' "Remember who you met" CTA (recap/pill
 * redesign §4.3): one canonical label, wired to the existing WeMetRosterDrawer
 * for an ended event. Home recap cards (§2) and the event-card family (§3)
 * drop this in instead of hand-rolling button + drawer.
 *
 * Two visual forms, one behavior:
 * - `chip` (default): the soft yellow h-8 chip in a true h-11 hit box — the
 *   Home recap card's standalone CTA (its card is question-card sized and not
 *   bound by the event-card envelope).
 * - `row` (device round 2, hard equality): a meta-row text action for
 *   SimpleEventCard — the exact box of the location row it replaces (text-xs
 *   16px line, the meta rows' gap-1.5, a w-3.5 icon), teal + Handshake (the
 *   We Met icon, TagFriendDrawer precedent). The ≥44px hit target comes from
 *   a non-layout ::before expansion, so the row's flow height stays one text
 *   line and the card never grows. (The Save-pill slot ships compact with no
 *   expansion; this is the strict upgrade.)
 *
 * CONTROLLED on purpose: the surface owns `open` because it also feeds
 * `useRecapMarkHidden`'s `deferWhile` — the drawer mounts inside the card's
 * subtree, and a hide flipping mid-flow would unmount the open drawer. The
 * house wiring:
 *
 *   const [rosterOpen, setRosterOpen] = useState(false);
 *   const { hidden, onMarked } = useRecapMarkHidden(event.id, 'home-recap', {
 *     deferWhile: rosterOpen,
 *   });
 *   if (hidden) return null;
 *   …
 *   <WeMetRecapCta
 *     eventId={event.id}
 *     eventName={event.name}
 *     open={rosterOpen}
 *     onOpenChange={next => {
 *       if (next) trackEvent(…); // surface-scoped analytics on open
 *       setRosterOpen(next);
 *     }}
 *     onMarked={onMarked}
 *   />
 *
 * Opening never hides — only a landed mark does (via `onMarked`).
 */
import type { MouseEvent } from 'react';
import { Handshake } from 'lucide-react';
import { cn } from '@lib/utils';
import { FOCUS_RING_CLASSES, PRESS_FEEDBACK_CLASSES } from './liveMotion';
import { WeMetRosterDrawer } from './WeMetRosterDrawer';

const LABEL = 'Remember who you met';

export interface WeMetRecapCtaProps {
  /** The ended event whose roster the CTA opens. */
  eventId: string;
  eventName: string;
  /** Controlled drawer visibility — the surface owns it (see the doc comment). */
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Forwarded to the drawer: fires per landed mark (see WeMetRosterDrawerProps). */
  onMarked?: () => void;
  /** Visual form — see the doc comment. Defaults to the standalone chip. */
  variant?: 'chip' | 'row';
  /** Extras for the tappable (per-surface placement, e.g. '-my-2'). */
  className?: string;
}

export function WeMetRecapCta({
  eventId,
  eventName,
  open,
  onOpenChange,
  onMarked,
  variant = 'chip',
  className,
}: WeMetRecapCtaProps) {
  const handleClick = (event: MouseEvent) => {
    // Recap CTAs sit inside tappable cards — never double-fire the card tap.
    event.stopPropagation();
    onOpenChange(true);
  };

  return (
    <>
      {variant === 'row' ? (
        <button
          type="button"
          onClick={handleClick}
          className={cn(
            // The location-row box (hard equality, device round 2): 16px
            // text-xs line, the meta rows' gap-1.5, a w-3.5 icon — the row's
            // flow height is exactly the text line it replaces.
            'relative flex w-fit items-center gap-1.5 rounded-sm text-xs font-semibold',
            // Teal = action (house link idiom); press tints to the dark step.
            'text-mb-teal transition-colors active:text-mb-teal-dark',
            // ≥44px hit target WITHOUT layout impact: 16px line + 2×14px = 44.
            "before:absolute before:-inset-x-2 before:-inset-y-3.5 before:content-['']",
            FOCUS_RING_CLASSES,
            className,
          )}
        >
          <Handshake className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          {LABEL}
        </button>
      ) : (
        <button
          type="button"
          onClick={handleClick}
          className={cn(
            // True ≥44px hit box around the visual h-8 chip (§4.1 hit areas —
            // the LiveCompactCard CTA-chip pattern).
            'flex h-11 shrink-0 items-center rounded-full',
            PRESS_FEEDBACK_CLASSES,
            FOCUS_RING_CLASSES,
            className,
          )}
        >
          <span className="flex h-8 items-center whitespace-nowrap rounded-full bg-mb-yellow-soft px-3 text-xs font-semibold text-gray-900">
            {LABEL}
          </span>
        </button>
      )}
      <WeMetRosterDrawer
        open={open}
        onOpenChange={onOpenChange}
        eventId={eventId}
        eventName={eventName}
        onMarked={onMarked}
      />
    </>
  );
}
